import { BrandStatus } from '@innovationdepartment/proxima-sdk-axios';
import { useToaster } from '@innovationdepartment/proxima-ui';
import { useProximaSDK, useShowSpinner } from 'hooks';
import { useState } from 'react';
import { useBrandStore } from 'stores';
import type { BrandOverviewProps } from 'types/brandOverview';
import BrandActionsConfirmLockUnlockModal from './BrandActions.ConfirmLockUnlockModal';
import BrandActionsView from './BrandActions.View';
import { getBrandActionSections } from './helpers';

const BrandActions = (props: BrandOverviewProps) => {
  const { brand, onRefreshBrandBilling } = props;
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const brandsApi = useProximaSDK('BrandsApi');
  const billingApi = useProximaSDK('BillingApi');
  const brandStore = useBrandStore();
  const { showToaster } = useToaster();

  const isLocked = brand.status === BrandStatus.Locked;

  useShowSpinner({ show: loading || brandsApi.loading || billingApi.loading });

  const onLockUnlockClick = () => {
    setShowConfirm(true);
  };

  const refreshBrand = async () => {
    const response = await brandsApi.getBrand({ brandId: brand.brandId });

    brandStore.updateBrandStore(response.data);
  };

  const requestWrapper = async (cb: () => Promise<void>) => {
    setLoading(true);

    try {
      await cb();
    } catch {
      /* */
    }
    setLoading(false);
  };

  const onTrialExtend = async (date: Date) => {
    if (!brand.brandId) return;

    await requestWrapper(async () => {
      await billingApi.extendTrial({
        brandId: brand.brandId,
        extendTrial: { endDate: date.toISOString() }
      });

      /* wait for backend to process events */
      await new Promise(resolve => setTimeout(resolve, 2000));

      /* refresh the data */
      await Promise.all([refreshBrand(), onRefreshBrandBilling()]);

      showToaster({
        message: 'Trial has been extended.',
        variant: 'success'
      });
    });
  };

  const onConfirmLockUnlock = async () => {
    setShowConfirm(false);

    await requestWrapper(async () => {
      if (isLocked) {
        await brandsApi.unlockBrand({ brandId: brand.brandId });
      } else {
        await brandsApi.lockBrand({
          brandId: brand.brandId,
          brandLockRequest: { audienceOption: 'keep_audiences' }
        });
      }

      await refreshBrand();

      showToaster({
        message: `Brand has been ${isLocked ? 'unlocked' : 'locked'} successfully.`,
        variant: isLocked ? 'success' : 'warning'
      });
    });
  };

  const sections = getBrandActionSections(props, { onLockUnlockClick, onTrialExtend });

  return (
    <>
      <BrandActionsView sections={sections} />
      <BrandActionsConfirmLockUnlockModal
        isLocked={isLocked}
        brand={brand}
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={onConfirmLockUnlock}
      />
    </>
  );
};

export default BrandActions;
